import { useState, useRef, useCallback, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Curated palette of colours suited for finance categories.
 * Organised in rows by hue for a visually coherent grid.
 */
const COLOR_PALETTE = [
  // Row 1 — reds / pinks
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  // Row 2 — greens
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  // Row 3 — blues
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  // Row 4 — purples / neutrals
  '#ec4899', '#f43f5e', '#78716c', '#6b7280', '#64748b',
];

// ---------------------------------------------------------------------------
// Colour-space helpers
// ---------------------------------------------------------------------------

/** Convert HSV (h 0-360, s 0-1, v 0-1) → hex string */
function hsvToHex(h: number, s: number, v: number): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
}

/** Convert hex string → HSV (h 0-360, s 0-1, v 0-1) */
function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

/** Hue (0-360) → pure hue hex */
function hueToHex(h: number): string {
  return hsvToHex(h, 1, 1);
}

// ---------------------------------------------------------------------------
// Gradient Picker sub-component (saturation-brightness square + hue strip)
// ---------------------------------------------------------------------------

interface GradientPickerProps {
  value: string;
  onChange: (hex: string) => void;
}

function GradientPicker({ value, onChange }: GradientPickerProps) {
  const svCanvasRef = useRef<HTMLCanvasElement>(null);
  const hueCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDraggingSV = useRef(false);
  const isDraggingHue = useRef(false);

  // Maintain HSV state internally to avoid hue flickering on conversion round-trips
  const hsvRef = useRef<{ h: number; s: number; v: number }>({ h: 0, s: 1, v: 1 });
  const [hsv, setHsv] = useState(() => {
    if (value && /^#[0-9A-Fa-f]{6}$/.test(value)) {
      const parsed = hexToHsv(value);
      hsvRef.current = parsed;
      return parsed;
    }
    return { h: 0, s: 1, v: 1 };
  });

  // When external value changes, sync only if it differs from our internal state
  useEffect(() => {
    if (value && /^#[0-9A-Fa-f]{6}$/.test(value)) {
      const current = hsvToHex(hsvRef.current.h, hsvRef.current.s, hsvRef.current.v);
      if (current.toLowerCase() !== value.toLowerCase()) {
        const parsed = hexToHsv(value);
        hsvRef.current = parsed;
        setHsv(parsed);
      }
    }
  }, [value]);

  // Draw saturation-brightness canvas
  useEffect(() => {
    const canvas = svCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    // White → hue horizontal gradient
    const hGrad = ctx.createLinearGradient(0, 0, w, 0);
    hGrad.addColorStop(0, '#ffffff');
    hGrad.addColorStop(1, hueToHex(hsv.h));
    ctx.fillStyle = hGrad;
    ctx.fillRect(0, 0, w, h);
    // Transparent → Black vertical gradient
    const vGrad = ctx.createLinearGradient(0, 0, 0, h);
    vGrad.addColorStop(0, 'rgba(0,0,0,0)');
    vGrad.addColorStop(1, '#000000');
    ctx.fillStyle = vGrad;
    ctx.fillRect(0, 0, w, h);
  }, [hsv.h]);

  // Draw hue strip canvas
  useEffect(() => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    for (let i = 0; i <= 6; i++) {
      grad.addColorStop(i / 6, hueToHex(i * 60));
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }, []);

  const updateFromSV = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = svCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
      const next = { ...hsvRef.current, s, v };
      hsvRef.current = next;
      setHsv(next);
      onChange(hsvToHex(next.h, next.s, next.v));
    },
    [onChange]
  );

  const updateFromHue = useCallback(
    (clientX: number) => {
      const canvas = hueCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const h = ratio * 360;
      const next = { ...hsvRef.current, h };
      hsvRef.current = next;
      setHsv(next);
      onChange(hsvToHex(next.h, next.s, next.v));
    },
    [onChange]
  );

  // Pointer event handlers for SV canvas
  const handleSVPointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDraggingSV.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updateFromSV(e.clientX, e.clientY);
    },
    [updateFromSV]
  );
  const handleSVPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingSV.current) return;
      updateFromSV(e.clientX, e.clientY);
    },
    [updateFromSV]
  );
  const handleSVPointerUp = useCallback(() => {
    isDraggingSV.current = false;
  }, []);

  // Pointer event handlers for Hue strip
  const handleHuePointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDraggingHue.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updateFromHue(e.clientX);
    },
    [updateFromHue]
  );
  const handleHuePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingHue.current) return;
      updateFromHue(e.clientX);
    },
    [updateFromHue]
  );
  const handleHuePointerUp = useCallback(() => {
    isDraggingHue.current = false;
  }, []);

  return (
    <div className="space-y-2">
      {/* SV gradient square */}
      <div className="relative w-full" style={{ aspectRatio: '1 / 0.75' }}>
        <canvas
          ref={svCanvasRef}
          width={240}
          height={180}
          className="w-full h-full rounded-md cursor-crosshair border"
          onPointerDown={handleSVPointerDown}
          onPointerMove={handleSVPointerMove}
          onPointerUp={handleSVPointerUp}
        />
        {/* Colour indicator */}
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow-md pointer-events-none -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            backgroundColor: hsvToHex(hsv.h, hsv.s, hsv.v),
          }}
        />
      </div>

      {/* Hue strip */}
      <div className="relative w-full h-3">
        <canvas
          ref={hueCanvasRef}
          width={240}
          height={16}
          className="w-full h-full rounded-full cursor-pointer"
          onPointerDown={handleHuePointerDown}
          onPointerMove={handleHuePointerMove}
          onPointerUp={handleHuePointerUp}
        />
        {/* Hue indicator */}
        <div
          className="absolute top-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow-md pointer-events-none -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${(hsv.h / 360) * 100}%`,
            backgroundColor: hueToHex(hsv.h),
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs for switching between Swatches and Gradient picker
// ---------------------------------------------------------------------------

type PickerTab = 'swatches' | 'gradient';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ColorPickerProps {
  /** Currently selected hex colour */
  value?: string;
  /** Callback when the user picks or clears a colour */
  onChange: (hex: string) => void;
  /** Extra className on the outer wrapper */
  className?: string;
}

export function ColorPicker({ value, onChange, className }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [customHex, setCustomHex] = useState('');
  const [tab, setTab] = useState<PickerTab>('swatches');

  const handleSelect = (hex: string) => {
    onChange(hex);
    setOpen(false);
  };

  const handleGradientChange = (hex: string) => {
    onChange(hex);
    setCustomHex(hex);
  };

  const handleCustomApply = () => {
    const hex = customHex.startsWith('#') ? customHex : `#${customHex}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      handleSelect(hex);
      setCustomHex('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            className="h-9 w-full justify-start text-left font-normal gap-2"
          >
            {value ? (
              <>
                <span
                  className="h-5 w-5 rounded-md border shrink-0"
                  style={{ backgroundColor: value }}
                />
                <span className="text-sm">{value}</span>
              </>
            ) : (
              <span className="text-muted-foreground">Pick a colour</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start" side="bottom">
          {/* Tab switcher */}
          <div className="flex gap-1 mb-3 p-0.5 bg-muted rounded-md">
            <button
              type="button"
              className={cn(
                'flex-1 text-xs font-medium py-1 rounded transition-colors',
                tab === 'swatches'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setTab('swatches')}
            >
              Swatches
            </button>
            <button
              type="button"
              className={cn(
                'flex-1 text-xs font-medium py-1 rounded transition-colors',
                tab === 'gradient'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setTab('gradient')}
            >
              Spectrum
            </button>
          </div>

          {tab === 'swatches' ? (
            /* Swatch grid */
            <div className="grid grid-cols-5 gap-1.5 mb-3">
              {COLOR_PALETTE.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={cn(
                    'h-8 w-8 rounded-md border transition-transform hover:scale-110 flex items-center justify-center',
                    value === hex && 'ring-2 ring-primary ring-offset-1'
                  )}
                  style={{ backgroundColor: hex }}
                  title={hex}
                  onClick={() => handleSelect(hex)}
                >
                  {value === hex && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}
                </button>
              ))}
            </div>
          ) : (
            /* Gradient / Spectrum picker */
            <div className="mb-3">
              <GradientPicker value={value || '#3b82f6'} onChange={handleGradientChange} />
            </div>
          )}

          {/* Hex input + preview */}
          <div className="flex items-center gap-1.5 pt-2 border-t">
            {value && (
              <span
                className="h-8 w-8 rounded-md border shrink-0"
                style={{ backgroundColor: value }}
              />
            )}
            <Input
              placeholder="#hexcode"
              value={customHex}
              maxLength={7}
              onChange={(e) => setCustomHex(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCustomApply()}
              className="h-8 text-sm"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-8 px-2"
              onClick={handleCustomApply}
              disabled={!/^#?[0-9A-Fa-f]{6}$/.test(customHex)}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear button */}
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={handleClear}
          aria-label="Clear colour"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export { COLOR_PALETTE };
